import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  User, Stethoscope, Calendar, Search, Filter, Star, Award,
  Clock, CheckCircle, ChevronRight, Phone, MessageCircle,
  Video, MapPin, GraduationCap, TrendingUp, Shield, Users,
  Heart, Sparkles, X, HelpCircle, DollarSign, Activity, Zap
} from 'lucide-react';
import Header from '../../common/Header';
import Footer from '../../common/Footer';
import doctorService from '../../../services/doctorService';

const DoctorList = () => {
  const [doctors, setDoctors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterSpecialization, setFilterSpecialization] = useState('all');
  const [sortBy, setSortBy] = useState('recommended');
  const [filteredDoctors, setFilteredDoctors] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    const loadDoctors = async () => {
      setLoading(true);
      try {
        const response = await doctorService.getDoctors();
        const doctorsList = response.data || response;
        // Filter to only show doctors with hospital consultation available
        const hospitalDoctors = Array.isArray(doctorsList) 
          ? doctorsList.filter(doc => doc.is_available_hospital_normal || doc.is_available_hospital_priority)
          : [];
        setDoctors(hospitalDoctors);
      } catch (err) {
        console.error('Failed to load doctors:', err);
        setDoctors([]);
      } finally {
        setLoading(false);
      }
    };

    loadDoctors();
  }, []);

  // Get unique specializations
  const specializations = ['all', ...new Set(doctors.map(d => d.specialization).filter(Boolean))];

  // Filter and sort logic
  useEffect(() => {
    let result = [...doctors];

    // Search filter
    if (searchQuery) {
      result = result.filter(doctor =>
        doctor.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        doctor.specialization?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        doctor.qualifications?.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    // Specialization filter
    if (filterSpecialization !== 'all') {
      result = result.filter(doctor => doctor.specialization === filterSpecialization);
    }

    // Sort
    if (sortBy === 'price-low') {
      result.sort((a, b) => parseFloat(a.price || 0) - parseFloat(b.price || 0));
    } else if (sortBy === 'price-high') {
      result.sort((a, b) => parseFloat(b.price || 0) - parseFloat(a.price || 0));
    } else if (sortBy === 'rating') {
      result.sort((a, b) => parseFloat(b.average_rating || 0) - parseFloat(a.average_rating || 0));
    } else if (sortBy === 'experience') {
      result.sort((a, b) => (b.experience_years || 0) - (a.experience_years || 0));
    }

    setFilteredDoctors(result);
  }, [searchQuery, filterSpecialization, sortBy, doctors]);

  const handleSelectDoctor = (doctor) => {
    navigate('/doctor-slots', { state: { doctor } });
  };

  if (loading) {
    return (
      <>
        <Header />
        <div className="min-h-screen bg-gradient-to-br from-teal-50 via-blue-50 to-indigo-50 flex items-center justify-center">
          <div className="text-center">
            <div className="relative">
              <div className="animate-spin rounded-full h-20 w-20 border-4 border-teal-200 border-t-teal-600 mx-auto"></div>
              <Stethoscope className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-8 h-8 text-teal-600 animate-pulse" />
            </div>
            <p className="mt-6 text-gray-700 text-lg font-medium">Finding best doctors for you...</p>
            <p className="mt-2 text-gray-500 text-sm">Connecting with healthcare professionals</p>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Header />
      <div className="min-h-screen bg-gradient-to-br from-teal-50 via-blue-50 to-indigo-50">

        {/* Hero Section */}
        <div className="relative bg-gradient-to-r from-teal-600 via-blue-600 to-indigo-600 text-white overflow-hidden">
          {/* Background Pattern */}
          <div className="absolute inset-0 opacity-10">
            <div className="absolute transform rotate-45 -left-20 -top-20 w-96 h-96 bg-white rounded-full"></div>
            <div className="absolute transform -rotate-12 right-0 top-40 w-72 h-72 bg-white rounded-full"></div>
            <div className="absolute transform rotate-180 left-1/3 bottom-0 w-64 h-64 bg-white rounded-full"></div>
          </div>

          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
            <div className="text-center max-w-4xl mx-auto">
              <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full mb-6">
                <Shield className="w-4 h-4" />
                <span className="text-sm font-medium">Verified & Experienced Doctors</span>
              </div>

              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-4 leading-tight">
                Book In-Person Consultation
                <span className="block text-teal-200">With Top Doctors</span>
              </h1>

              <p className="text-lg md:text-xl text-teal-100 mb-8 max-w-2xl mx-auto">
                Connect with experienced healthcare professionals for personalized care.
                Book your appointment in just a few clicks.
              </p>

              {/* Search Bar */}
              <div className="max-w-2xl mx-auto mb-8">
                <div className="relative">
                  <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
                  <input
                    type="text"
                    placeholder="Search by doctor name, specialization, qualifications..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-12 pr-14 py-4 rounded-2xl text-gray-900 shadow-2xl focus:outline-none focus:ring-4 focus:ring-teal-300 text-lg"
                  />
                  {searchQuery && (
                    <button
                      type="button"
                      onClick={() => setSearchQuery('')}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 p-2 rounded-full bg-gray-200 hover:bg-gray-300 text-gray-600 hover:text-gray-900 transition-all duration-200 flex items-center justify-center"
                      style={{ background: 'rgb(229, 231, 235)', width: 'auto', border: 'none' }}
                      aria-label="Clear search"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>

              {/* Quick Stats */}
              <div className="grid grid-cols-3 gap-4 md:gap-8 max-w-3xl mx-auto">
                <div className="text-center">
                  <div className="text-3xl md:text-4xl font-bold mb-1">{doctors.length}+</div>
                  <div className="text-sm md:text-base text-teal-100">Expert Doctors</div>
                </div>
                <div className="text-center border-x border-white/30">
                  <div className="text-3xl md:text-4xl font-bold mb-1">4.9★</div>
                  <div className="text-sm md:text-base text-teal-100">Average Rating</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl md:text-4xl font-bold mb-1">24/7</div>
                  <div className="text-sm md:text-base text-teal-100">Available</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Filter Bar */}
        <div className="bg-white border-b sticky top-0 z-10 shadow-sm">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
              {/* Specialization Filter */}
              <div className="flex items-center gap-3 flex-wrap">
                <span className="text-sm font-medium text-gray-700 flex items-center gap-2">
                  <Filter className="w-4 h-4" />
                  Specialization:
                </span>
                <select
                  value={filterSpecialization}
                  onChange={(e) => setFilterSpecialization(e.target.value)}
                  className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 bg-white text-sm"
                >
                  <option value="all">All Specializations</option>
                  {specializations.filter(s => s !== 'all').map((spec, idx) => (
                    <option key={idx} value={spec}>{spec}</option>
                  ))}
                </select>
              </div>

              {/* Sort & Results */}
              <div className="flex items-center gap-3 flex-wrap">
                <span className="text-sm font-medium text-gray-700">
                  {filteredDoctors.length} Doctor{filteredDoctors.length !== 1 ? 's' : ''}
                </span>
                {searchQuery && (
                  <span className="text-xs bg-teal-100 text-teal-700 px-2 py-1 rounded-full">
                    for "{searchQuery}"
                  </span>
                )}
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 bg-white text-sm"
                >
                  <option value="recommended">Recommended</option>
                  <option value="rating">Highest Rated</option>
                  <option value="experience">Most Experienced</option>
                  <option value="price-low">Price: Low to High</option>
                  <option value="price-high">Price: High to Low</option>
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          {filteredDoctors.length === 0 ? (
            <div className="text-center py-20">
              <div className="bg-white rounded-2xl shadow-xl p-12 max-w-md mx-auto">
                <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Search className="w-12 h-12 text-gray-400" />
                </div>
                <h3 className="text-2xl font-bold text-gray-800 mb-3">No Doctors Found</h3>
                <p className="text-gray-600 mb-6">
                  {searchQuery
                    ? `No doctors found for "${searchQuery}". Try different keywords.`
                    : 'No doctors available at the moment.'
                  }
                </p>
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery('')}
                    className="px-6 py-3 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors"
                  >
                    Clear Search
                  </button>
                )}
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredDoctors.map((doctor) => {
                const rating = parseFloat(doctor.average_rating || 4.5);
                const reviewCount = doctor.total_reviews || 0;
                const experience = doctor.experience_years || 0;
                // Use hospital pricing for appointment bookings
                const normalPrice = parseFloat(doctor.price_hospital_normal || doctor.price || 0);
                const priorityPrice = parseFloat(doctor.price_hospital_priority || doctor.price || 0);
                const hasVideo = doctor.video_call_enabled;
                const isAvailable = doctor.is_available_now;

                return (
                  <div
                    key={doctor.id}
                    className="group bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden border-2 border-gray-100 hover:border-teal-200 flex flex-col h-full"
                  >
                    {/* Header with Image */}
                    <div className="relative bg-gradient-to-br from-teal-50 to-blue-50 p-6">
                      {/* Availability Badge */}
                      {isAvailable && (
                        <div className="absolute top-4 right-4 bg-green-500 text-white px-3 py-1.5 rounded-full shadow-lg flex items-center gap-1">
                          <Activity className="w-3 h-3 animate-pulse" />
                          <span className="text-xs font-bold">Available Now</span>
                        </div>
                      )}

                      {/* Profile Image */}
                      <div className="relative w-32 h-32 mx-auto mb-4">
                        {doctor.profile_image ? (
                          <img
                            src={`https://www.homecureexpress.com${doctor.profile_image}`}
                            alt={doctor.name}
                            className="w-full h-full rounded-full object-cover border-4 border-white shadow-xl"
                            onError={(e) => {
                              e.target.style.display = 'none';
                              e.target.nextSibling.style.display = 'flex';
                            }}
                          />
                        ) : null}
                        <div
                          className="w-full h-full rounded-full bg-gradient-to-br from-teal-500 to-blue-500 flex items-center justify-center border-4 border-white shadow-xl"
                          style={{ display: doctor.profile_image ? 'none' : 'flex' }}
                        >
                          <User className="w-16 h-16 text-white" />
                        </div>

                        {/* Video Badge */}
                        {hasVideo && (
                          <div className="absolute bottom-0 right-0 bg-gradient-to-r from-purple-500 to-pink-500 text-white p-2 rounded-full shadow-lg">
                            <Video className="w-5 h-5" />
                          </div>
                        )}
                      </div>

                      {/* Name & Specialization */}
                      <div className="text-center">
                        <h3 className="text-xl font-bold text-gray-900 mb-2">
                          {doctor.name}
                        </h3>
                        <div className="inline-block bg-gradient-to-r from-teal-600 to-blue-600 text-white px-4 py-1.5 rounded-full text-sm font-medium shadow-md">
                          {doctor.specialization}
                        </div>
                      </div>
                    </div>

                    {/* Content */}
                    <div className="flex-1 flex flex-col p-6">
                      {/* Stats Grid */}
                      <div className="grid grid-cols-2 gap-3 mb-4">
                        {experience > 0 && (
                          <div className="flex items-center gap-2 text-sm text-gray-600">
                            <GraduationCap className="w-4 h-4 text-teal-600 flex-shrink-0" />
                            <span className="font-medium">{experience}+ Years</span>
                          </div>
                        )}
                        <div className="flex items-center gap-2 text-sm text-gray-600">
                          <Star className="w-4 h-4 text-yellow-500 flex-shrink-0 fill-current" />
                          <span className="font-medium">{rating.toFixed(1)} ({reviewCount})</span>
                        </div>
                        {hasVideo && (
                          <div className="flex items-center gap-2 text-sm text-gray-600">
                            <Video className="w-4 h-4 text-purple-600 flex-shrink-0" />
                            <span className="font-medium">Video Call</span>
                          </div>
                        )}
                        <div className="flex items-center gap-2 text-sm text-gray-600">
                          <MapPin className="w-4 h-4 text-red-600 flex-shrink-0" />
                          <span className="font-medium">In-Person</span>
                        </div>
                      </div>

                      {/* Qualifications */}
                      {doctor.qualifications && (
                        <div className="bg-gray-50 rounded-lg p-3 mb-4">
                          <p className="text-xs text-gray-600 line-clamp-2">
                            <span className="font-semibold">Qualifications: </span>
                            {doctor.qualifications}
                          </p>
                        </div>
                      )}

                      {/* Bio */}
                      {doctor.bio && (
                        <p className="text-sm text-gray-600 leading-relaxed mb-4 line-clamp-3">
                          {doctor.bio}
                        </p>
                      )}

                      {/* Features */}
                      <div className="grid grid-cols-2 gap-2 mb-4">
                        <div className="flex items-center gap-1.5 text-xs text-gray-600">
                          <CheckCircle className="w-3.5 h-3.5 text-green-500 flex-shrink-0" />
                          <span>Verified</span>
                        </div>
                        <div className="flex items-center gap-1.5 text-xs text-gray-600">
                          <CheckCircle className="w-3.5 h-3.5 text-green-500 flex-shrink-0" />
                          <span>Licensed</span>
                        </div>
                        <div className="flex items-center gap-1.5 text-xs text-gray-600">
                          <CheckCircle className="w-3.5 h-3.5 text-green-500 flex-shrink-0" />
                          <span>Quick Booking</span>
                        </div>
                        <div className="flex items-center gap-1.5 text-xs text-gray-600">
                          <CheckCircle className="w-3.5 h-3.5 text-green-500 flex-shrink-0" />
                          <span>Easy Reschedule</span>
                        </div>
                      </div>

                      {/* Pricing & Action */}
                      <div className="border-t pt-4 mt-auto">
                        <div className="flex items-center justify-between mb-4">
                          <div>
                            <p className="text-xs text-gray-500 mb-1">Consultation Fee</p>
                            <div className="flex items-baseline gap-2">
                              {normalPrice === priorityPrice ? (
                                <span className="text-3xl font-bold text-teal-600">
                                  ₹{normalPrice}
                                </span>
                              ) : (
                                <div className="flex flex-col">
                                  <span className="text-2xl font-bold text-teal-600">
                                    ₹{normalPrice} - ₹{priorityPrice}
                                  </span>
                                  <span className="text-xs text-gray-500 mt-1">Normal/Priority</span>
                                </div>
                              )}
                            </div>
                          </div>
                          <div className="text-center">
                            <div className="flex items-center gap-1 text-yellow-500 mb-1">
                              <Star className="w-5 h-5 fill-current" />
                              <span className="text-lg font-bold text-gray-700">{rating.toFixed(1)}</span>
                            </div>
                            <p className="text-xs text-gray-500">{reviewCount} reviews</p>
                          </div>
                        </div>

                        {/* Book Button */}
                        <button
                          onClick={() => handleSelectDoctor(doctor)}
                          className="w-full py-3 px-4 rounded-xl font-semibold transition-all duration-200 flex items-center justify-center gap-2 bg-gradient-to-r from-teal-600 to-blue-600 text-white hover:from-teal-700 hover:to-blue-700 shadow-lg group-hover:shadow-xl"
                        >
                          <Calendar className="w-5 h-5" />
                          <span>Book Appointment</span>
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Why Choose Us Section */}
        <div className="bg-white border-y">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Why Choose Our Doctors?
              </h2>
              <p className="text-gray-600 max-w-2xl mx-auto">
                Trusted healthcare professionals committed to your wellbeing
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              <div className="text-center group">
                <div className="w-20 h-20 bg-gradient-to-br from-teal-100 to-blue-100 rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300 shadow-lg">
                  <Shield className="w-10 h-10 text-teal-600" />
                </div>
                <h3 className="font-bold text-gray-900 mb-2 text-lg">Verified Professionals</h3>
                <p className="text-gray-600 text-sm leading-relaxed">
                  All doctors are verified with proper credentials and licenses
                </p>
              </div>

              <div className="text-center group">
                <div className="w-20 h-20 bg-gradient-to-br from-purple-100 to-pink-100 rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300 shadow-lg">
                  <Award className="w-10 h-10 text-purple-600" />
                </div>
                <h3 className="font-bold text-gray-900 mb-2 text-lg">Experienced Specialists</h3>
                <p className="text-gray-600 text-sm leading-relaxed">
                  Years of experience in their respective specializations
                </p>
              </div>

              <div className="text-center group">
                <div className="w-20 h-20 bg-gradient-to-br from-green-100 to-emerald-100 rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300 shadow-lg">
                  <Clock className="w-10 h-10 text-green-600" />
                </div>
                <h3 className="font-bold text-gray-900 mb-2 text-lg">Flexible Scheduling</h3>
                <p className="text-gray-600 text-sm leading-relaxed">
                  Book appointments at your convenient time and location
                </p>
              </div>

              <div className="text-center group">
                <div className="w-20 h-20 bg-gradient-to-br from-blue-100 to-indigo-100 rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300 shadow-lg">
                  <Heart className="w-10 h-10 text-blue-600" />
                </div>
                <h3 className="font-bold text-gray-900 mb-2 text-lg">Patient-Centered Care</h3>
                <p className="text-gray-600 text-sm leading-relaxed">
                  Personalized treatment plans focused on your health goals
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* How It Works Section */}
        <div className="bg-gradient-to-br from-teal-50 to-blue-50 py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                How It Works
              </h2>
              <p className="text-gray-600 max-w-2xl mx-auto">
                Book your appointment in 4 simple steps
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              {[
                { num: '1', icon: <Search className="w-8 h-8" />, title: 'Find Doctor', desc: 'Search by specialization or symptoms' },
                { num: '2', icon: <Calendar className="w-8 h-8" />, title: 'Select Slot', desc: 'Choose convenient date and time' },
                { num: '3', icon: <CheckCircle className="w-8 h-8" />, title: 'Confirm Booking', desc: 'Complete your appointment booking' },
                { num: '4', icon: <Stethoscope className="w-8 h-8" />, title: 'Visit Clinic', desc: 'Meet doctor at scheduled time' }
              ].map((step, idx) => (
                <div key={idx} className="relative">
                  <div className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-shadow">
                    <div className="w-16 h-16 bg-gradient-to-br from-teal-500 to-blue-500 text-white rounded-2xl flex items-center justify-center text-2xl font-bold mb-4 shadow-lg">
                      {step.num}
                    </div>
                    <div className="text-teal-600 mb-3">{step.icon}</div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">{step.title}</h3>
                    <p className="text-gray-600 text-sm leading-relaxed">{step.desc}</p>
                  </div>
                  {idx < 3 && (
                    <div className="hidden md:block absolute top-1/2 -right-3 transform -translate-y-1/2 z-10">
                      <ChevronRight className="w-6 h-6 text-teal-300" />
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Emergency Contact */}
        <div className="bg-gradient-to-r from-red-600 to-pink-600 text-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
            <div className="flex flex-col md:flex-row items-center justify-between gap-6">
              <div className="text-center md:text-left">
                <div className="flex items-center gap-3 justify-center md:justify-start mb-2">
                  <Phone className="w-6 h-6 animate-pulse" />
                  <h3 className="text-2xl font-bold">Need Urgent Consultation?</h3>
                </div>
                <p className="text-red-100">Our team is available 24/7 for emergency appointments</p>
              </div>
              <a
                href="tel:+919876543210"
                className="bg-white text-red-600 px-8 py-4 rounded-xl font-bold text-lg hover:bg-red-50 transition-colors shadow-2xl flex items-center gap-2 group"
              >
                <Phone className="w-5 h-5 group-hover:animate-bounce" />
                <span>Call: +91 98765 43210</span>
              </a>
            </div>
          </div>
        </div>

        {/* FAQ Section */}
        <div className="bg-white py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <div className="inline-flex items-center gap-2 bg-teal-100 text-teal-700 px-4 py-2 rounded-full mb-4">
                <HelpCircle className="w-4 h-4" />
                <span className="text-sm font-medium">Got Questions?</span>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Frequently Asked Questions
              </h2>
              <p className="text-gray-600 max-w-2xl mx-auto">
                Everything you need to know about booking appointments
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
              {/* Support Card */}
              <div className="relative order-2 lg:order-1">
                <div className="sticky top-24">
                  <div className="relative bg-gradient-to-br from-teal-100 to-blue-100 rounded-3xl p-8 shadow-2xl overflow-hidden">
                    <div className="relative z-10 bg-white rounded-2xl p-8 shadow-xl">
                      <div className="flex flex-col items-center justify-center text-center space-y-6">
                        <div className="flex items-end gap-4 mb-4">
                          <div className="w-24 h-24 bg-gradient-to-br from-teal-500 to-blue-500 rounded-full flex items-center justify-center shadow-lg">
                            <Users className="w-12 h-12 text-white" />
                          </div>
                          <div className="w-32 h-32 bg-gradient-to-br from-blue-500 to-indigo-500 rounded-full flex items-center justify-center shadow-lg">
                            <Stethoscope className="w-16 h-16 text-white" />
                          </div>
                        </div>

                        <div className="space-y-3">
                          <h3 className="text-2xl font-bold text-gray-900">Need Help?</h3>
                          <p className="text-gray-600 leading-relaxed">
                            Our support team is ready to help you find the right doctor
                          </p>
                        </div>

                        <div className="w-full space-y-3 pt-4">
                          <a
                            href="tel:+919876543210"
                            className="w-full flex items-center justify-center gap-3 bg-gradient-to-r from-teal-600 to-blue-600 text-white px-6 py-3 rounded-xl font-semibold hover:from-teal-700 hover:to-blue-700 transition-all shadow-lg"
                          >
                            <Phone className="w-5 h-5" />
                            <span>Call: +91 98765 43210</span>
                          </a>
                          <a
                            href="https://wa.me/919876543210?text=Hi%2C%20I%20need%20help%20booking%20a%20doctor%20appointment"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="w-full flex items-center justify-center gap-3 bg-white border-2 border-teal-600 text-teal-600 px-6 py-3 rounded-xl font-semibold hover:bg-teal-50 transition-all"
                          >
                            <MessageCircle className="w-5 h-5" />
                            <span>WhatsApp Chat</span>
                          </a>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* FAQs */}
              <div className="space-y-4 order-1 lg:order-2">
                {[
                  {
                    q: "How do I book an appointment?",
                    a: "Simply browse our doctors, select your preferred specialist, choose a convenient time slot, and confirm your booking. You'll receive instant confirmation via SMS and email."
                  },
                  {
                    q: "Can I reschedule my appointment?",
                    a: "Yes, you can reschedule your appointment up to 2 hours before the scheduled time without any charges. Go to 'My Appointments' and select reschedule."
                  },
                  {
                    q: "What should I bring to the appointment?",
                    a: "Please bring your medical history, previous prescriptions, lab reports (if any), and a valid ID proof. Arrive 10 minutes early for registration."
                  },
                  {
                    q: "Are video consultations available?",
                    a: "Yes, many of our doctors offer video consultations. Look for the video call badge on doctor profiles. You can book video consultations from our online consultation section."
                  },
                  {
                    q: "What are the consultation fees?",
                    a: "Consultation fees vary by doctor and specialization, typically ranging from ₹300-₹1000. The exact fee is displayed on each doctor's profile."
                  },
                  {
                    q: "How do I cancel an appointment?",
                    a: "You can cancel your appointment from 'My Appointments' section. Cancellations made 4+ hours before the appointment are eligible for full refund."
                  }
                ].map((faq, index) => (
                  <details key={index} className="group bg-gray-50 rounded-2xl overflow-hidden shadow-md hover:shadow-lg transition-shadow">
                    <summary className="flex items-start justify-between cursor-pointer p-6 hover:bg-gray-100 transition-colors">
                      <div className="flex items-start gap-3 flex-1">
                        <div className="mt-1 flex-shrink-0">
                          <HelpCircle className="w-5 h-5 text-teal-600" />
                        </div>
                        <span className="font-semibold text-gray-900 text-left pr-4">{faq.q}</span>
                      </div>
                      <ChevronRight className="w-5 h-5 text-gray-400 group-open:rotate-90 transition-transform flex-shrink-0 mt-1" />
                    </summary>
                    <div className="px-6 pb-6 pl-14 text-gray-600 leading-relaxed">
                      {faq.a}
                    </div>
                  </details>
                ))}
              </div>
            </div>
          </div>
        </div>

      </div>
      <Footer />
    </>
  );
};

export default DoctorList;
